import { AutoAwesomeOutlined, AutoModeOutlined } from '@mui/icons-material';
import { Button, Divider, styled, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { useFetchData } from '../../hooks/useFetchData';
import { useGetElementSize } from '../../hooks/useGetElementSize';
import { usePromptDrawer } from '../../hooks/usePromptDrawer';
import { getFolder } from '../../redux/actions/folderActions';
import { findFeaturedPrompts } from '../../redux/actions/promptActions';
import { myFolder } from '../../redux/consts';
import { selectAppStatus } from '../../redux/reducers/appStatusReducer';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { selectPrompts } from '../../redux/reducers/promptReducer';
import { AppDispatch } from '../../redux/store';
import { LinkDiv } from '../../theme/CustomComponents';
import { Conversation } from '../../Types/conversation';
import { ProjectWithLastScanned } from '../../Types/dealGPT';
import { AppEnum, PaginationMode, PromptLocation } from '../../Types/enums';
import { Folder as FolderType } from '../../Types/folder';
import { Prompt } from '../../Types/prompt';
import { conversationsPathUrl, projectPathUrl } from '../../util/routesUrls';
import ChatCard from '../Chat/ChatCard';
import ChatInput from '../Chat/ChatInput';
import DealGPTContainer from '../DealGPT/ViewModels/DealGPTContainer';
import DebouncedSearchInput from '../DebouncedSearchInput';
import LoadingMask from '../LoadingMask';
import PromptCard from '../Prompts/PromptCard';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import { useAuth } from '../Providers/AuthProvider';

const LoadMoreButton = styled(Button)({
  margin: '8px 16px',
  color: 'black',
  fontWeight: 'bold',
  width: '250px',
});

const SearchContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '16px',
  marginRight: '20px',
  marginTop: '5px',
  '@media (max-width: 895px)': {
    width: '430px',
  },
});

const Chats = (): JSX.Element => {
  const navigate = useClassicNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const analytics = useAnalytics();
  const { isDealGPTAdmin, isAdmin } = useAuth();

  const { selectedApp } = useSelector(selectAppStatus);
  const { currentFolder } = useSelector(selectFolders);
  const { featuredPrompts } = useSelector(selectPrompts);
  const { height: chatInputHeight } = useGetElementSize('chats-chat-input');

  const [searchQuery, setSearchQuery] = useState('');
  const [conversationSearchSelected, setConversationSearchSelected] = useState(false);

  const { data: project } = useFetchData<ProjectWithLastScanned>({
    urlPath: projectPathUrl(currentFolder?.projectId ?? '', isDealGPTAdmin),
    skip: !currentFolder?.projectId,
  });

  const {
    data: conversations,
    pagination,
    nextPage,
    resetPagination,
    refetch: refetchConversations,
    loading,
  } = useFetchData<Conversation>({
    urlPath: conversationsPathUrl(selectedApp, searchQuery, false, currentFolder?.id),
    paginated: true,
    paginationMode: PaginationMode.APPEND,
  });

  const shouldShowLoadMoreButton = pagination?.hasNextPage ?? false;

  // Open the prompt drawer if a promptId is provided in the URL
  usePromptDrawer({
    autoOpenFromUrl: true,
    shouldRefetchFeaturedPrompts: currentFolder?.id === myFolder.id,
  });

  const isInitialMount = useRef(true);
  const currentFolderIdRef = useRef<string | null>(null);

  const processFolderChange = useCallback(async () => {
    if (!currentFolder?.id) {
      navigate(`/folders/${myFolder.id}`);
      return;
    }

    if (currentFolder.id === myFolder.id) {
      setSearchQuery('');
      dispatch(findFeaturedPrompts());
    } else {
      const action = await dispatch(getFolder({ folderId: currentFolder.id, isAdmin }));
      const folder = action.payload as FolderType;

      if (folder) {
        setSearchQuery('');
        setConversationSearchSelected(false);
      } else {
        navigate(`/folders/${myFolder.id}`);
      }
    }
  }, [dispatch, navigate, currentFolder?.id, isAdmin]);

  useEffect(() => {
    if (currentFolderIdRef.current === currentFolder?.id) {
      return;
    }

    currentFolderIdRef.current = currentFolder?.id || null;

    if (isInitialMount.current) {
      isInitialMount.current = false;
    }

    processFolderChange();

    return () => {};
  }, [currentFolder?.id, processFolderChange]);

  const loadMoreConversations = () => {
    analytics.event(AnalyticsEvent.WM_NIGEL_LOAD_MORE_CONVERSATIONS, undefined, {
      numConversationsOld: conversations?.length,
      folderId: currentFolder?.id,
    });
    nextPage();
  };

  const handleSearchUpdate = useCallback(
    (value: string) => {
      setSearchQuery(value);
      resetPagination();
      if (selectedApp == AppEnum.NIGEL) {
        analytics.event(
          AnalyticsEvent.WM_NIGEL_FOLDER_SEARCH,
          { value, id: currentFolder?.id },
          {
            folderId: currentFolder?.id,
            searchQuery: value,
          }
        );
      } else if (selectedApp == AppEnum.DEAL_GPT) {
        analytics.event(
          AnalyticsEvent.WM_IA_FOLDER_SEARCH,
          { value, id: currentFolder?.id },
          {
            folderId: currentFolder?.id,
            projectId: currentFolder?.projectId,
            searchQuery: value,
          }
        );
      }
    },
    [resetPagination, currentFolder, selectedApp, analytics]
  );

  if (currentFolder?.projectId && !project) {
    return (
      <DealGPTContainer>
        <LoadingMask isLoading={true} />
      </DealGPTContainer>
    );
  }
  return (
    <div style={{ minWidth: '950px', height: '100%' }}>
      <LoadingMask isLoading={loading} />
      <div id="chats-chat-input" style={{ marginRight: '20px' }}>
        <ChatInput hideWarnings={true} project={project as ProjectWithLastScanned} />
      </div>
      {currentFolder?.id !== myFolder.id ? (
        <div>
          <SearchContainer>
            <DebouncedSearchInput
              value={searchQuery}
              valueUpdated={handleSearchUpdate}
              onFocus={() => setConversationSearchSelected(true)}
              onBlur={() => setConversationSearchSelected(false)}
              shouldFocus={conversationSearchSelected}
            />
          </SearchContainer>
          {loading ? (
            <LoadingMask isLoading={true} />
          ) : conversations?.length === 0 ? (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <Typography variant="subtitle2">No conversations at the moment.</Typography>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                overflowY: 'auto',
                gap: 15,
                paddingBottom: '20px',
                maxHeight: `calc(100vh - 450px - var(--banner-height, 0px) - ${
                  selectedApp === AppEnum.DEAL_GPT ? '75px' : '0px'
                })`,
              }}
            >
              {conversations?.map((conversation: Conversation) => (
                <ChatCard
                  searchQuery={searchQuery}
                  key={conversation.id}
                  conversation={conversation}
                  IsSearchFocused={false}
                  refetchConversations={refetchConversations}
                  onConversationCardClick={() => {
                    analytics.event(
                      AnalyticsEvent.WM_NIGEL_CONVERSATION_CLICK,
                      { id: conversation.id },
                      {
                        folderId: conversation.folderId ?? myFolder.id,
                        conversationId: conversation.id,
                        projectId: currentFolder?.projectId,
                      }
                    );
                    navigate(`/folder/${conversation.folderId ?? myFolder.id}/${conversation.id}`);
                  }}
                />
              ))}
              {shouldShowLoadMoreButton && (
                <LoadMoreButton onClick={loadMoreConversations}>Load More +</LoadMoreButton>
              )}
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            height: `calc(100% - ${chatInputHeight}px )`,
            overflowY: 'auto',
          }}
        >
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            {featuredPrompts.length > 0 && (
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '5px',
                    marginBottom: '10px',
                  }}
                >
                  <AutoModeOutlined sx={{ color: '#50658E', height: '20px', width: '20px' }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      marginBottom: '10px',
                      color: '#50658E',
                      fontSize: '15px',
                      fontWeight: '700',
                    }}
                  >
                    Featured Prompts
                  </Typography>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '15px',
                    width: 'fit-content',
                    maxHeight: '100px',
                    paddingRight: '15px',
                    paddingBottom: '10px',
                  }}
                >
                  {featuredPrompts.map((prompt: Prompt) => (
                    <PromptCard
                      key={prompt.id}
                      prompt={prompt}
                      location={PromptLocation.FEATURED_PROMPTS}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '5px',
                }}
              >
                <AutoAwesomeOutlined sx={{ color: '#50658E', height: '20px', width: '20px' }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#50658E',
                    fontWeight: '700',
                    fontSize: '15px',
                  }}
                >
                  In the Spotlight
                </Typography>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '15px',
                  width: 'fit-content',
                  maxHeight: '120px',
                  marginRight: '15px',
                }}
              >
                <div
                  style={{
                    padding: '10px',
                    width: '300px',
                    height: '130px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  <Typography variant="caption1" sx={{ fontWeight: '600' }}>
                    🎓 GenAI 201: Working Strategically with Nigel
                  </Typography>
                  <Typography variant="caption1">
                    Advance your AI skills by customizing Nigel, organizing with folders, and using
                    tools like Deep Research and Web Search.
                  </Typography>
                  <LinkDiv sx={{ mr: '5px', display: 'inline' }}>
                    <Typography
                      variant="caption1"
                      sx={{ color: '#0047FF' }}
                      onClick={() => {
                        window.open('https://westmonroe.degreed.app/plan/3680856', '_blank');
                      }}
                    >
                      {`Explore the Assessment Plan →`}
                    </Typography>
                  </LinkDiv>
                </div>
                <Divider orientation="vertical" flexItem variant="fullWidth" />
                <div
                  style={{
                    padding: '10px',
                    width: '300px',
                    height: '130px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  <Typography variant="caption1" sx={{ fontWeight: '600' }}>
                    {`🧠 Go Deeper with Nigel's Deep Research`}
                  </Typography>
                  <Typography variant="caption1">
                    Nigel handles the heavy lifting. Deep Research searches the web, analyzes
                    sources, and delivers concise, credible insights for your projects.
                  </Typography>
                  <LinkDiv sx={{ mr: '5px', display: 'inline' }}>
                    <Typography
                      variant="caption1"
                      sx={{ color: '#0047FF' }}
                      onClick={() => {
                        window.open('https://howto.westmonroe.com/a/1997526', '_blank');
                      }}
                    >
                      {`Learn More →`}
                    </Typography>
                  </LinkDiv>
                </div>
                <Divider orientation="vertical" flexItem variant="fullWidth" />
                <div
                  style={{
                    padding: '10px',
                    width: '300px',
                    height: '130px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  <Typography variant="caption1" sx={{ fontWeight: '600' }}>
                    {`🎉 Nigel's Raffle Winners!`}
                  </Typography>
                  <Typography variant="caption1">
                    Congrats to our winners, and thanks to everyone who joined the celebration!
                  </Typography>
                  <Typography variant="caption1" sx={{}}>
                    🏆 Mandi Hayes
                  </Typography>
                  <Typography variant="caption1" sx={{}}>
                    🥈 Hallie Murray & Joshua Copeland
                  </Typography>
                  <Typography variant="caption1" sx={{ display: 'flex' }}>
                    🥉 Marie Splaine, Josh Bolinger & Whitney Reddan
                  </Typography>
                  <Typography variant="caption1" sx={{ marginTop: '5px' }}></Typography>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chats;
